// Shared video-quality (capture resolution) preference
// (wiki/feature-recording-video-quality.md).
//
// VoiceDojo is a *video* practice studio, so some use cases want a high-fidelity
// capture — up to 4K. This store holds the resolution tier surfaced as a
// `SegmentedToggle` on the single shared `RecordingPanel`. Like the rest of the
// recording stack the choice is purely client-side: it sets `getUserMedia`
// constraints (as `ideal`) and the `MediaRecorder` bitrate; nothing is uploaded
// and no backend is involved.
//
// Default = the user's PREFERRED quality; reset before every take. The saved
// preferred quality is the single source of truth: a logged-in user's
// server-side setting (`UserState.defaultRecordingQuality`, carried on whoami,
// fed in via `setPreferred`); a guest is `DEFAULT_QUALITY_TIER`. The picker re-applies the
// preferred value before each take. A per-take change (`set`) is an IN-MEMORY
// override for THAT take only — it records at the picked tier but is never
// persisted to localStorage, and `resetToPreferred()` (run at the pre-take
// boundary) returns the live tier to the preferred so the next take starts from
// the preferred value. There is NO device-local persisted pick.
//
// Companion `deviceMaxHeight` sub-store (NON-persisted): the camera's real
// maximum capture height, learned from the acquired track's `getCapabilities()`
// the first time a take starts (we never force an early permission prompt on
// mount just to probe). Once known, the picker only offers tiers the device can
// deliver, and the effective selection clamps down to the device's highest
// supported tier. This is a per-device, per-session capability verdict, not a
// preference — so it is never persisted, and starts `null` (unknown) each
// session. The pure helpers (`supportedTiers`, `effectiveTier`, `bitrateFor`,
// `constraintsFor`) are exported DOM-free so they're unit-testable.

import { writable, type Readable } from 'svelte/store';
import { DefaultRecordingQuality } from '@voicedojo/backend-types';

/** A resolution-ladder tier key. Kept in ascending order for simple clamping. */
export type QualityTier = '480p' | '720p' | '1080p' | '1440p' | '2160p';

export interface QualityTierSpec {
  /** Human label shown in the picker. */
  readonly label: string;
  /** Target capture width (applied as an `ideal` getUserMedia constraint). */
  readonly width: number;
  /** Target capture height (applied as an `ideal` getUserMedia constraint). */
  readonly height: number;
  /**
   * Target `MediaRecorder.videoBitsPerSecond`. Bitrate scales with resolution
   * or the higher tiers look no better than 720p — the whole point of the
   * picker. The encoder caps this to what it can actually do.
   */
  readonly bitrate: number;
}

/**
 * The resolution ladder, in ASCENDING order (lowest → highest). Filtering to
 * "tiers the device supports" and clamping a too-high pick down both rely on
 * this order. Bitrates are a sensible starting point (see the wiki table).
 */
export const QUALITY_TIERS: Record<QualityTier, QualityTierSpec> = {
  '480p': { label: '480p', width: 854, height: 480, bitrate: 1_500_000 },
  '720p': { label: '720p (HD)', width: 1280, height: 720, bitrate: 3_000_000 },
  '1080p': { label: '1080p (Full HD)', width: 1920, height: 1080, bitrate: 6_000_000 },
  '1440p': { label: '1440p (2K)', width: 2560, height: 1440, bitrate: 10_000_000 },
  '2160p': { label: '2160p (4K)', width: 3840, height: 2160, bitrate: 28_000_000 },
};

/** The ladder keys in ascending order — the canonical iteration order. */
export const QUALITY_TIER_ORDER: readonly QualityTier[] = [
  '480p',
  '720p',
  '1080p',
  '1440p',
  '2160p',
];

/** The preferred tier for a guest (no server-side setting). Also the fallback
 *  whenever a non-tier preferred value is fed in. Single-sourced from the
 *  backend `UserDefaults.RecordingQuality` via the generated constant, so a
 *  guest and a fresh account start on the same tier. */
export const DEFAULT_QUALITY_TIER: QualityTier = DefaultRecordingQuality;

/** Type guard: is `value` one of the ladder keys? */
function isQualityTier(value: unknown): value is QualityTier {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(QUALITY_TIERS, value)
  );
}

/**
 * The compact badge label for a recorded-quality tier — the tier KEY itself (e.g.
 * `"1080p"`), NOT the long marketing label (`"1080p (Full HD)"`). Returns `null` for a
 * null / unknown / non-ladder value so callers omit the badge. The single source of truth
 * for the quality badge shown on the post-take card and the recent-takes card.
 */
export function qualityBadgeLabel(tier: string | null | undefined): string | null {
  return isQualityTier(tier) ? tier : null;
}

// ── Pure helpers (DOM-free, unit-testable) ──────────────────────────────────

/**
 * The tiers a device with the given camera-max height can actually deliver,
 * in ascending order. When `maxHeight` is unknown (null) we offer ALL tiers —
 * we don't probe the camera early just to filter. When the device max is below
 * the lowest tier, we still keep the lowest tier so the picker is never empty
 * (the `ideal` constraint degrades it gracefully).
 */
export function supportedTiers(maxHeight: number | null): QualityTier[] {
  if (maxHeight == null || !Number.isFinite(maxHeight)) {
    return [...QUALITY_TIER_ORDER];
  }
  const offered = QUALITY_TIER_ORDER.filter(
    (tier) => QUALITY_TIERS[tier].height <= maxHeight,
  );
  // Never an empty picker — a sub-480p camera still gets the lowest tier
  // (capture degrades gracefully via the `ideal` constraint).
  return offered.length > 0 ? offered : [QUALITY_TIER_ORDER[0]];
}

/**
 * The EFFECTIVE tier given the user's pick and the device max: the pick,
 * clamped DOWN to the highest tier the device supports. (Never clamps up — a
 * device that can do more than the user asked for still records at the pick.)
 */
export function effectiveTier(
  picked: QualityTier,
  maxHeight: number | null,
): QualityTier {
  const offered = supportedTiers(maxHeight);
  if (offered.includes(picked)) return picked;
  // Picked tier exceeds the device max — clamp to the highest supported tier
  // (offered is ascending, so the last element is the highest).
  return offered[offered.length - 1];
}

/** The target `videoBitsPerSecond` for a tier. */
export function bitrateFor(tier: QualityTier): number {
  return QUALITY_TIERS[tier].bitrate;
}

/** When background blur is on, recording is limited to this height — blur
 *  segmentation + canvas composite is heavy (engine default is 480p), so the
 *  honest sustainable ceiling is 720p. Single tunable constant. */
export const BLUR_QUALITY_CAP_HEIGHT = 720;

/** The max capture height once the blur cap is folded in. Blur off → just the
 *  device max (may be null = unknown). Blur on → min(deviceMax, cap), and the
 *  cap still applies when the device max is unknown. */
export function cappedMaxHeight(deviceMaxHeight: number | null, blurOn: boolean): number | null {
  if (!blurOn) return deviceMaxHeight;
  return deviceMaxHeight == null
    ? BLUR_QUALITY_CAP_HEIGHT
    : Math.min(deviceMaxHeight, BLUR_QUALITY_CAP_HEIGHT);
}

/**
 * The `getUserMedia` video-constraint fragment for a tier — `ideal` width +
 * height so a weaker camera degrades gracefully to its best instead of failing
 * `getUserMedia` (never `exact`). Caller spreads this into its `video` object
 * alongside any other constraints (facingMode, etc.).
 */
export function constraintsFor(tier: QualityTier): MediaTrackConstraints {
  const spec = QUALITY_TIERS[tier];
  return {
    width: { ideal: spec.width },
    height: { ideal: spec.height },
  };
}

export interface VideoQualityStore extends Readable<QualityTier> {
  /**
   * Companion sub-store: the learned camera-max capture height (px), or null
   * until a take has started and `getCapabilities()` reported it. NOT persisted
   * (a per-device, per-session capability verdict). `$`-subscribable.
   */
  readonly deviceMaxHeight: Readable<number | null>;
  /**
   * Per-take override — set the live tier for THIS take only. In-memory, never
   * persisted; `resetToPreferred()` (run at the pre-take boundary) discards it.
   */
  set(tier: QualityTier): void;
  /**
   * Set the PREFERRED tier — the single source of truth the picker resets to
   * before every take. Fed from the user's server-side default recording
   * quality (whoami's `defaultRecordingQuality`); a guest / null / non-tier
   * value falls back to `DEFAULT_QUALITY_TIER`. When no per-take override is in effect for the
   * current pre-take, the live tier follows the new preferred (so the picker
   * reflects the user's setting once whoami resolves). Not persisted.
   */
  setPreferred(tier: string | null | undefined): void;
  /**
   * Re-apply the preferred tier as the live value, discarding any per-take
   * override. Run at the pre-take boundary so every fresh take starts at the
   * preferred quality.
   */
  resetToPreferred(): void;
  /**
   * Record the camera's learned max capture height (from `getCapabilities()`),
   * so the picker can filter to supported tiers + clamp. Ignores a non-positive
   * / non-finite value. Not persisted.
   */
  setDeviceMaxHeight(height: number | null): void;
  /** Non-reactive snapshot of `{ tier, deviceMaxHeight }`. */
  snapshot(): { tier: QualityTier; deviceMaxHeight: number | null };
}

function createVideoQualityStore(): VideoQualityStore {
  // The preferred tier — the single source of truth the picker resets to before
  // every take. Fed from whoami via setPreferred; the default tier until then (and for a
  // guest). In-memory only — never persisted.
  let preferred: QualityTier = DEFAULT_QUALITY_TIER;
  // True once a per-take `set()` override is in effect for the current pre-take.
  // Cleared by `resetToPreferred()` at the take boundary. While false, a new
  // preferred value flows straight through to the live tier.
  let overridden = false;

  // The live effective pick — starts at the preferred.
  const tier = writable<QualityTier>(preferred);
  // Capability verdict — null (unknown) every session until a take starts and
  // the acquired track's getCapabilities() reports a height.max. Never persisted.
  const deviceMaxHeight = writable<number | null>(null);

  // Non-reactive reads of the live in-memory values (avoids a get() import).
  let tierNow: QualityTier = preferred;
  let deviceMaxHeightNow: number | null = null;
  tier.subscribe((v) => (tierNow = v));
  deviceMaxHeight.subscribe((v) => (deviceMaxHeightNow = v));

  return {
    subscribe: tier.subscribe,
    deviceMaxHeight: { subscribe: deviceMaxHeight.subscribe },

    set(next: QualityTier): void {
      // Per-take override — in-memory only, never persisted.
      overridden = true;
      tier.set(next);
    },

    setPreferred(next: string | null | undefined): void {
      // A guest / null / non-tier value → the default preferred tier.
      preferred = isQualityTier(next) ? next : DEFAULT_QUALITY_TIER;
      // With no per-take override in effect, the live tier follows the preferred
      // (so the picker reflects the user's setting once whoami resolves).
      if (!overridden) tier.set(preferred);
    },

    resetToPreferred(): void {
      overridden = false;
      tier.set(preferred);
    },

    setDeviceMaxHeight(height: number | null): void {
      if (height == null || !Number.isFinite(height) || height <= 0) {
        return;
      }
      deviceMaxHeight.set(Math.round(height));
    },

    snapshot(): { tier: QualityTier; deviceMaxHeight: number | null } {
      return { tier: tierNow, deviceMaxHeight: deviceMaxHeightNow };
    },
  };
}

export const videoQualityStore = createVideoQualityStore();
